import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "@ui5/webcomponents/dist/Toast.js";
import "@ui5/webcomponents/dist/Bar.js";
import "@ui5/webcomponents/dist/Label.js";
import "@ui5/webcomponents/dist/Input.js";
import "@ui5/webcomponents/dist/Button.js";
import { Bar, Pie, Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { TablePagination } from '@mui/material';
import localforage from 'localforage';

Chart.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const ProgressBar = ({ familiesEncoded, totalFamilies }) => {
  const percentage = totalFamilies !== 0 ? Math.round((familiesEncoded / totalFamilies) * 100) : 0;
  const progressBarColor = percentage === 100 ? '#4CAF50' : '#2196F3';

  return (
    <div style={{
      width: '100%',
      backgroundColor: '#e0e0e0',
      borderRadius: '10px',
      overflow: 'hidden',
      height: '15px',
      position: 'relative'
    }}>
      <div style={{
        height: '100%',
        width: `${percentage}%`,
        backgroundColor: progressBarColor,
        transition: 'width 0.5s ease-in-out'
      }}></div>
      <span style={{
        position: 'absolute',
        right: '5px',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        color: '#333'
      }}>{percentage}%</span>
    </div>
  );
};

const selectStyle = {
  fontFamily: 'sans-serif',
  fontSize: '1rem',
  padding: '0.5rem 1rem',
  borderRadius: '8px',
  border: '1px solid #ccc',
  backgroundColor: '#fff',
  color: '#333',
  appearance: 'none', 
  WebkitAppearance: 'none', 
  MozAppearance: 'none', 
  cursor: 'pointer',
  
  width: '100%',
  boxSizing: 'border-box',
  
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  transition: 'border-color 0.2s, box-shadow 0.2s',
}

const containerStyle = {
  width: '100%',
  margin: '1rem auto',
  fontFamily: 'Arial, sans-serif',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  overflow: 'hidden'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse'
};

const thTdStyle = {
  padding: '1rem',
  textAlign: 'left',
  borderBottom: '1px solid #ddd'
};

const thStyle = {
  ...thTdStyle,
  backgroundColor: '#f4f7f9',
  fontWeight: 'bold',
  color: '#333',
  textTransform: 'uppercase',
  fontSize: '0.85rem'
};

const tdStyle = {
  ...thTdStyle,
  color: '#555'
};

const centerTextStyle = {
  textAlign: 'center'
};

const Overview = ({ onRowClick: onOverviewRowClickProp }) => {
  const userRole = parseInt(localStorage.getItem("userRole"), 10);
  // const userRole = '2';
  const barangayCode = localStorage.getItem('barangayCode');
  // const barangayCode = '10944'
  const municipalCode = localStorage.getItem('municipalCode');
  // const municipalCode = '422';
  const provincialCode = localStorage.getItem('provincialCode');
  // const provincialCode = '19';
  let baseUrl = `${process.env.REACT_APP_API_URL}/api/locations/api/householddata`
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    first_name: '',
    last_name: '',
    barangay: '',
    municipality: ''
  });
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);
  const [barangayName, setBarangayName] = useState("");
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page when rows per page changes
  };
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const [provinces, setProvinces] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [municipals, setMunicipals] = useState([]);
  const [selectedMunicipal, setSelectedMunicipal] = useState('');
  const [barangayData, setBarangayData] = useState([]);

  const [locationPopup, setLocationPopup] = useState(false);

  const navigate = useNavigate();

  const toastRef = useRef(null);

  const showToast = useCallback((message) => {
    if (toastRef.current) {
      toastRef.current.textContent = message;
      toastRef.current.open = true;
    }
  }, []);

  const fetchFamilyHeads = useCallback(async () => {
    try {
      setIsLoading(true);
      const userEmail = localStorage.getItem("userEmail");
      const userId = localStorage.getItem("userId");
      // const userRole = localStorage.getItem("userRole");
      // console.log(userRole,"-",userId)

      if (!userEmail) {
        console.error("❌ User email not found in localStorage.");
        showToast("User email not found. Please log in again.");
        setIsLoading(false);
        setTableData([]);
        setFilteredData([]);
        return;
      }
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/family-heads?email=${encodeURIComponent(userEmail)}`;

      const response = await fetch(apiUrl, {
        headers: {
          "x-api-key": process.env.REACT_APP_API_KEY
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ API response error:", response.status, errorText);
        setTableData([]);
        setFilteredData([]);
        showToast("Error fetching data. Please try again.");
        return;
      }

      const rawData = await response.json();

      if (!Array.isArray(rawData)) {
        console.error("⚠️ Unexpected API response format:", rawData);
        setTableData([]);
        setFilteredData([]);
        showToast("Unexpected data format received.");
        return;
      }

      let processedData = rawData;
      // // console.log(processedData)
      // if (String(userRole) === "5") {
      //   const filteredForRole = rawData.filter(item => String(item.created_by) === String(userId));
      //   // console.log("Role 5 filtering applied:", {
      //   //   userRole,
      //   //   userId,
      //   //     originalCount: rawData.length,
      //   //     filteredCount: filteredForRole.length
      //   // });
      //   processedData = filteredForRole;
      //   if (filteredForRole.length === 0) {
      //     setTableData([]);
      //     setFilteredData([]);
      //     showToast("No records available for this user.");
      //     return;
      //   }
      // }

      if (processedData.length > 0) {
        if ((parseInt(userRole) === 4 || parseInt(userRole) === 5) && barangayCode) {
          processedData = processedData.filter(item => item.barangay_id === parseInt(barangayCode));
        } else if (parseInt(userRole) === 3 && municipalCode) {
          processedData = processedData.filter(item => item.municipality_id === parseInt(municipalCode));
        } else if (parseInt(userRole) === 2 && provincialCode) {
          processedData = processedData.filter(item => item.province_id === parseInt(provincialCode));
        }
        setTableData(processedData);
        setFilteredData(processedData);
        showToast("Data refreshed successfully!");
      } else {
        setTableData([]);
        setFilteredData([]);
        showToast("No data available.");
      }
    } catch (error) {
      console.error("🔥 Error fetching family heads:", error);
      setTableData([]);
      setFilteredData([]);
      showToast("Network error. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  const handleInputChange = async (e, index) => {
    const updatedData = [...barangayData];
    const newValue = e.target.value;
    let parsedValue = parseInt(newValue, 10);
    if (isNaN(parsedValue) || parsedValue < 0) {
      parsedValue = 0;
      e.target.value = parsedValue;
      return
    }

    const oldValue = updatedData[index].total;

    if ((oldValue == newValue) || (oldValue === null && newValue === '0')) {
      return
    }

    const location = `${selectedProvince.province_name}_${selectedMunicipal.municipality_name}_${updatedData[index].barangay_name}`
    const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}`;
    const response = await fetch(apiUrl,
        {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({new_value: newValue, old_value: oldValue}),
        }
    );

    const result = await response.json();

    if (!response.ok) {
        // Log the detailed error from the server if available
        console.error("Server error response:", result);
        throw new Error(result.message || `Failed to save data. Status: ${response.status}`);
    } else {
      updatedData[index].total = parseInt(newValue, 10) || 0;
      setBarangayData(updatedData);
    }
  };

  const fetchBarangayData = async () => {
      try {
        const location = `${selectedProvince.province_name}_${selectedMunicipal.municipality_name}`;
        // console.log(location)
        const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}`;
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'x-api-key': process.env.REACT_APP_API_KEY
          }
        });

        // Check for a successful response status (200-299)
        if (!response.ok) {
          const errorText = await response.text(); // Read the error message as plain text
          console.error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
          return; // Exit the function to prevent further errors
        }

        const data = await response.json();
        // console.log(data)
        if (Array.isArray(data) && data.length > 0) {
          if (barangayName !== '') {
            setBarangayData(data.filter(b => b.barangay_name === barangayName));
          } else {
            setBarangayData(data);
          }
          
          return data;
        } else {
          console.warn(`API returned an empty array or non-array data. Data received:`, data);
        }
      } catch (error) {
        console.error(`Error fetching barangay data:`, error);
      }
  }

  const provincialItemLookupUsingProvincialCode = async (provincesData, code) => {
    const provinceItem = provincesData.data.find(p => p.province_id === parseInt(code));
    setSelectedProvince(provinceItem);
  }

  const provincialItemLookupUsingMunicipalCode = async (provincesData, code) => {
    const municipalitiesData = await localforage.getItem('location_municipality');
    const provinceContainer = municipalitiesData.data.find(mun => mun.municipalities.find(m => m.municipality_id === parseInt(code)));
    const provinceId = provinceContainer.municipalities.find(m => m.municipality_id === parseInt(code)).province_id;

    provincialItemLookupUsingProvincialCode(provincesData, provinceId);
  }

  const provincialItemLookupUsingBarangayCode = async (provincesData) => {
    const barangaysData = await localforage.getItem('location_barangay');
    const municipalityContainer = barangaysData.data.find(bar => bar.barangays.find(b => b.barangay_id === parseInt(barangayCode)));
    const municipalityId = municipalityContainer.barangays.find(b => b.barangay_id === parseInt(barangayCode)).municipality_id;

    provincialItemLookupUsingMunicipalCode(provincesData, municipalityId);
  }

  const municipalItemLookupUsingMunicipalCode = async (municipalitiesData, code) => {
    const provinceContainer = municipalitiesData.data.find(mun => mun.municipalities.find(m => m.municipality_id === parseInt(code)));
    const municipalityItem = provinceContainer.municipalities.find(m => m.municipality_id === parseInt(code));
    setSelectedMunicipal(municipalityItem);
  }

  const municipalItemLookupUsingBarangayCode = async (municipalitiesData) => {
    const barangaysData = await localforage.getItem('location_barangay');
    const municipalityContainer = barangaysData.data.find(bar => bar.barangays.find(b => b.barangay_id === parseInt(barangayCode)));
    const barangayItem = municipalityContainer.barangays.find(b => b.barangay_id === parseInt(barangayCode));
    const barangayName = barangayItem.barangay_name;
    const municipalityId = barangayItem.municipality_id;
    setBarangayName(barangayName);
    municipalItemLookupUsingMunicipalCode(municipalitiesData, municipalityId);
  }

  useEffect(() => {
    fetchFamilyHeads();
  }, [fetchFamilyHeads]);

  useEffect(() => {
    const filtered = tableData.filter(row => {
      const first_name = String(row.first_name || '').toLowerCase();
      const last_name = String(row.last_name || '').toLowerCase();
      const barangay = String(row.barangay || '').toLowerCase();
      const municipality = String(row.municipality || '').toLowerCase();

      return (
        first_name.includes((filters.first_name || '').toLowerCase()) &&
        last_name.includes((filters.last_name || '').toLowerCase()) &&
        barangay.includes((filters.barangay || '').toLowerCase()) &&
        municipality.includes((filters.municipality || '').toLowerCase())
      );
    });
    setFilteredData(filtered);
  }, [filters, tableData]);

  // Fetch provinces
  useEffect(() => {
    const fetchProvinces = async () => {
        const cacheProvinceKey = 'location_province';
        try {
            const data = await localforage.getItem(cacheProvinceKey)
            setProvinces(data.data);
            if (parseInt(userRole) === 2 && provincialCode) {
              provincialItemLookupUsingProvincialCode(data, provincialCode)
            } else if (parseInt(userRole) === 3 && municipalCode) {
              provincialItemLookupUsingMunicipalCode(data, municipalCode)
            } else if ((parseInt(userRole) === 4 || parseInt(userRole) === 5) && barangayCode) {
              provincialItemLookupUsingBarangayCode(data);

            } else {
              setSelectedProvince({}); // Reset province when region changes
            }
            setMunicipals([]);       // Clear municipals
            setSelectedMunicipal({}); // Reset municipal
        } catch (error) {
            console.error('Error fetching provinces:', error);
        }
    };
    fetchProvinces();
  }, []);
  
  // Fetch municipals (cities-municipalities) when a province is selected
  useEffect(() => {
    if (selectedProvince) {
        const cacheMunicipalityKey = 'location_municipality';
        const fetchMunicipals = async () => {
            try {
                const data = await localforage.getItem(cacheMunicipalityKey);
                const filteredData = Array.isArray(data.data)
                ? data.data.find(item => item.province_id === Number(selectedProvince.province_id))?.municipalities
                : [];
                setMunicipals(filteredData); 
                if (parseInt(userRole) === 3 && municipalCode) {
                  municipalItemLookupUsingMunicipalCode(data, municipalCode);
                } else if ((parseInt(userRole) === 4 || parseInt(userRole) === 5) && barangayCode) {
                  municipalItemLookupUsingBarangayCode(data);
                } else {
                  setSelectedMunicipal({}); // Reset municipal when province changes
                }
            } catch (error) {
                console.error('Error fetching municipals:', error);
            }
        }
        fetchMunicipals();
    } else {
        setMunicipals([]);
        setSelectedMunicipal({});
    }
  }, [selectedProvince, provinces]);

  useEffect(() => {
    fetchBarangayData();
  }, [selectedMunicipal])

  const handleFilterChange = (e, field) => {
    setFilters({
      ...filters,
      [field]: e.target.value
    });
  };

  const handleClearFilters = () => {
    setFilters({
      first_name: '',
      last_name: '',
      barangay: '',
      municipality: ''
    });
  };

  const handleMuiRowClick = (row) => {
    if (row && row.head_id) {
      navigate(`/detail/${row.head_id}`, { state: { rowData: row } });
    } else {
      showToast("Could not find data for the selected row.");
      console.warn("Clicked row is missing head_id or row data.", row);
    }

    if (onOverviewRowClickProp) {
      onOverviewRowClickProp(row);
    }
  };

  const [dashboardData, setDashboardData] = useState({
    age: null,
    sex: null,
    civilStatus: null,
    vulnerable: null,
    education: null,
    employment: null,
    householdSize: null,
  });
  const [showDashboard, setShowDashboard] = useState(true);

  const LocationPopup = () => {
    return <div
        style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.35)",
            zIndex: 1000,
        }}
        >
        <div
            style={{
                backgroundColor: "white",
                padding: "10px 20px",
                borderRadius: "12px",
                boxShadow: "0 12px 20px rgba(0,0,0,0.35)",
                width: "320px",
                maxWidth: "80vw",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "5px",
                textAlign: "center",
            }}
        >
            <h2>Select Location</h2>
            <div style={{display: "flex", flexDirection: "column", gap: "10px"}}>
              <select
                value={selectedProvince?.province_id || "Select Province"}
                onChange={(e) => {
                    const provinceId = e.target.value;
                    const selected = provinces.find(p => p.province_id === Number(provinceId));
                    setSelectedProvince(selected ? selected : {});
                }}
                disabled={parseInt(userRole) !== 1}
                style={selectStyle}
              >
                <option value={0}>Select Province</option>
                {Array.isArray(provinces) && provinces.length > 0 && provinces.map((province) => (
                  <option key={province.province_id} value={province.province_id}>
                    {province.province_name}
                  </option>
                ))}
              </select>

              <select
                value={Object.keys(selectedMunicipal).length !== 0 ? selectedMunicipal.municipality_id : "Select Municipality"}
                onChange={(e) => {
                  const municipalityId = e.target.value;
                  const selected = municipals.find(m => m.municipality_id === Number(municipalityId));
                  setSelectedMunicipal(selected ? selected : {})

                  // If user clears the search query after it was applied, reset the applied state
                  // if (isSearchApplied && e.target.value.trim() === '') {
                  //   setIsSearchApplied(false);
                  //   setSearchQuery('');
                  //   setAppliedLocationData({}); // Clear applied location data
                  // }
                }}
                style={selectStyle}
                disabled={Object.keys(selectedProvince).length === 0 || (parseInt(userRole) !== 1 && parseInt(userRole) !== 2)} // Disable if no province is selected
              >
                <option value={0}>Select Municipal</option>
                {Array.isArray(municipals) && municipals.length > 0 && municipals.map((municipal) => (
                  <option key={municipal.municipality_id} value={municipal.municipality_id}>
                    {municipal.municipality_name}
                  </option>
                ))}
              </select>
            </div>

            <button
              style={{
                  padding: "10px 15px",
                  borderRadius: "6px",
                  margin: "9px auto",
                  border: "none",
                  backgroundColor: "#2563eb",
                  color: "white",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#1d4ed8")}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#2563eb")}
              onClick={() => {
                setLocationPopup(false)}
              }
              >
              Okay
            </button>
        </div>
    </div>
  }

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const userEmail = localStorage.getItem("userEmail");

        if (!userEmail) {
          console.error("❌ User email not found in localStorage for dashboard.");
          setDashboardData({
            age: null,
            sex: null,
            civilStatus: null,
            vulnerable: null,
            education: null,
            employment: null,
            householdSize: null,
            error: "User email not found. Please log in again."
          });
          setShowDashboard(false);
          return;
        }

        const apiUrl = `${process.env.REACT_APP_API_URL}/api/upload/reports/summary?email=${encodeURIComponent(userEmail)}`;

        const resp = await fetch(apiUrl, {
          headers: { "x-api-key": process.env.REACT_APP_API_KEY }
        });
        if (resp.ok) {
          const data = await resp.json();
          setDashboardData(data);
          setShowDashboard(true);
        } else {
          const errorText = await resp.text();
          console.error("❌ API response error for dashboard summary:", resp.status, errorText);
          setDashboardData({
            age: null,
            sex: null,
            civilStatus: null,
            vulnerable: null,
            education: null,
            employment: null,
            householdSize: null,
            error: `Error fetching dashboard: ${resp.status}`
          });
          setShowDashboard(false);
        }
      } catch (e) {
        console.error("🔥 Error fetching dashboard summary:", e);
        setDashboardData({
          age: null,
            sex: null,
            civilStatus: null,
            vulnerable: null,
            education: null,
            employment: null,
            householdSize: null,
            error: "Network error fetching dashboard."
        });
        setShowDashboard(false);
      }
    };
    fetchDashboard();
  }, []);

  const hasData = (obj) =>
    obj && Array.isArray(obj.labels) && obj.labels.length > 0 && Array.isArray(obj.counts) && obj.counts.length > 0;

  if (isLoading) {
    return <div style={{ textAlign: 'center', marginTop: '2rem' }}>Loading...</div>;
  }

  // if (tableData.length === 0 && !isLoading) {
  //   return (
  //     <div style={{ textAlign: 'center', marginTop: '2rem' }}>
  //       <p>No data available</p>
  //       <ui5-button onClick={fetchFamilyHeads}>Refresh Data</ui5-button>
  //     </div>
  //   );
  // }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
      <ui5-toast ref={toastRef}></ui5-toast>
      <div style={{
        position: 'sticky',
        top: '0',
        zIndex: '1000',
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }}>
        <ui5-bar design="Header" style={{ width: '100%' }}>
          <div slot="startContent" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            width: '100%',
            justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <ui5-label>Filters:</ui5-label>
              <ui5-input
                placeholder="First Name"
                value={filters.first_name}
                onInput={(e) => handleFilterChange(e, 'first_name')}
                style={{ width: '180px' }}
              ></ui5-input>
              <ui5-input
                placeholder="Last Name"
                value={filters.last_name}
                onInput={(e) => handleFilterChange(e, 'last_name')}
                style={{ width: '180px' }}
              ></ui5-input>
            </div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <ui5-input
                placeholder="Barangay"
                value={filters.barangay}
                onInput={(e) => handleFilterChange(e, 'barangay')}
                style={{ width: '180px' }}
              ></ui5-input>
              <ui5-input
                placeholder="Municipality"
                value={filters.municipality}
                onInput={(e) => handleFilterChange(e, 'municipality')}
                style={{ width: '180px' }}
              ></ui5-input>
              <ui5-button design="Transparent" onClick={handleClearFilters}>Clear</ui5-button>
              <ui5-button design="Emphasized" onClick={fetchFamilyHeads}>Refresh Data</ui5-button>
            </div>
          </div>
        </ui5-bar>
      </div>
      <div style={{ marginBottom: '2rem' }}>
        <h2>Barangay Dashboard Overview</h2>
        <div style={{ gap: '2rem', display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "60vh 60vh" }}>
          <div style={{
            flex: '1 1 300px',
            minWidth: 300,
            backgroundColor: '#ffffff',
            borderRadius: '12px', // Softer corners
            padding: '1.5rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', // Subtle shadow
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h4 style={{
              // Modernized title styles
              margin: '0 0 1rem 0',
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#333'
            }}>
              Age Distribution
            </h4>
            <div style={{ flex: 1 }}> {/* Flex container for chart to grow */}
              {hasData(dashboardData.age) ? (
                <Bar
                  data={{
                    labels: dashboardData.age.labels,
                    datasets: [{
                      label: 'Population',
                      data: dashboardData.age.counts,
                      backgroundColor: '#5A96E3', // A modern, friendly blue
                      borderRadius: 6, 
                      borderSkipped: false,
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false }
                    },
                    scales: {
                      x: {
                        grid: {
                          display: false, 
                        },
                      },
                      y: {
                        grid: {
                          color: '#f0f0f0', 
                          drawBorder: false,
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div style={{
                  minHeight: 200,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {/* {!dashboardData.age && <CircularProgress size={24} />} */}
                  <span>
                    {dashboardData.age ? "No data available" : "Loading..."}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div style={{
            flex: '1 1 300px', 
            minWidth: 250,
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h4 style={{
              margin: '0 0 1rem 0',
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#333'
            }}>
              Sex Distribution
            </h4>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
              {hasData(dashboardData.sex) ? (
                <Pie
                  data={{
                    labels: dashboardData.sex.labels,
                    datasets: [{
                      data: dashboardData.sex.counts,
                      backgroundColor: ['#5A96E3', '#FF8C42'], 
                      borderColor: '#ffffff', 
                      borderWidth: 2,
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'left',
                        labels: {
                          color: '#666',
                          boxWidth: 20,
                          padding: 20,
                          font: {
                            size: 14
                          }
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {/* {!dashboardData.sex && <CircularProgress size={24} />} */}
                  <span>
                    {dashboardData.sex ? "No data available" : "Loading..."}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div style={{
            flex: '1 1 300px',
            minWidth: 250,
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h4 style={{
              margin: '0 0 1rem 0',
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#333'
            }}>
              Civil Status
            </h4>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
              {hasData(dashboardData.civilStatus) ? (
                <Doughnut
                  data={{
                    labels: dashboardData.civilStatus.labels,
                    datasets: [{
                      data: dashboardData.civilStatus.counts,
                      // A modern, multi-color palette
                      backgroundColor: ['#5A96E3', '#FF8C42', '#8E7CC3', '#4CAF50'],
                      borderColor: '#ffffff',
                      borderWidth: 2,
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'left',
                        labels: {
                          color: '#666',
                          boxWidth: 20,
                          padding: 20,
                          font: {
                            size: 14
                          }
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div style={{
                  // Consistent placeholder styles
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {/* {!dashboardData.civilStatus && <CircularProgress size={24} />} */}
                  <span>
                    {dashboardData.civilStatus ? "No data available" : "Loading..."}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div style={{
            flex: '1 1 300px',
            minWidth: 250,
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h4 style={{
              margin: '0 0 1rem 0',
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#333'
            }}>
              Vulnerable Groups
            </h4>
            <div style={{ flex: 1 }}> 
              {hasData(dashboardData.vulnerable) ? (
                <Bar
                  data={{
                    labels: dashboardData.vulnerable.labels,
                    datasets: [{
                      label: 'Count',
                      data: dashboardData.vulnerable.counts,
                      backgroundColor: '#4CAF50',
                      borderRadius: 6,
                      borderSkipped: false,
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false }
                    },
                    scales: {
                      x: {
                        grid: {
                          display: false, 
                        },
                      },
                      y: {
                        grid: {
                          color: '#f0f0f0', 
                          drawBorder: false,
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div style={{
                  minHeight: 200,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {/* {!dashboardData.vulnerable && <CircularProgress size={24} />} */}
                  <span>
                    {dashboardData.vulnerable ? "No data available" : "Loading..."}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div style={containerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th 
                style={{...thStyle, color: "white", cursor: "pointer", backgroundColor: "#5889f2ff"}} 
                onClick={() => setLocationPopup(true) } 
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#7292ebff")}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#5889f2ff")}
              >{(Object.keys(selectedMunicipal).length !== 0 && Object.keys(selectedProvince).length !== 0) ? `${selectedMunicipal.municipality_name}, ${selectedProvince.province_name}` : "Select Municipality"}</th> 
              <th style={{ ...thStyle, ...centerTextStyle }}>Household Count</th>
              <th style={{ ...thStyle, ...centerTextStyle }}>Families Encoded</th>
              <th style={{ ...thStyle, ...centerTextStyle }}>Total Families</th>
              <th style={{...thStyle, ...centerTextStyle}}>Progress</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(barangayData).length !== 0 ? barangayData.map((row, index) => (
              <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white' }}>
                <td style={tdStyle}>{row.barangay_name}</td>
                <td style={{ ...tdStyle, ...centerTextStyle }}>{row.household_count}</td>
                <td style={{ ...tdStyle, ...centerTextStyle }}>{row.family_count}</td>
                <td style={{ ...tdStyle, ...centerTextStyle }}>
                  <input 
                  disabled={parseInt(userRole) !== 1 && parseInt(userRole) !== 3} 
                  type="number"
                  value={row.total || 0} 
                  onChange={(e) => handleInputChange(e, index)}
                  onKeyDown={(e) => {
                    // Prevents typing of decimal points
                    if (e.key === '.' || e.key === ',') {
                      e.preventDefault();
                    }
                  }}
                  style={{
                    width: '80px',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    textAlign: 'center'
                  }}/>
                </td>
                <td style={tdStyle}>
                  <ProgressBar
                    familiesEncoded={row.family_count}
                    totalFamilies={row.total ? row.total : 0}
                  />
                </td>
              </tr>
            )) : 
              <tr>
                 <td colSpan={5} style={{...tdStyle, textAlign: "center"}}>No Data</td>
              </tr>}
          </tbody>
        </table>
      </div>
      {locationPopup && <LocationPopup />}
      <TableContainer component={Paper} style={{ marginTop: '1rem', boxShadow: '0 0 0.125rem 0 rgba(0,0,0,0.1)' }}>
        <Table sx={{ minWidth: 650 }} aria-label="family heads table">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>HeadID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>First Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Last Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Barangay</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Municipality</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Relationship</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Main Family ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row) => (
                <TableRow
                  key={row.head_id}
                  hover
                  onClick={() => handleMuiRowClick(row)}
                  style={{ cursor: 'pointer' }}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {row.head_id}
                  </TableCell>
                  <TableCell>{row.first_name || '-'}</TableCell>
                  <TableCell>{row.last_name || '-'}</TableCell>
                  <TableCell>{row.barangay || '-'}</TableCell>
                  <TableCell>{row.municipality || '-'}</TableCell>
                  <TableCell>{row.relationship || "Head"}</TableCell>
                  <TableCell>{row.extended_head_id || "NA"}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {/* Add the navigator component here */}
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={filteredData.length} // Total number of items
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </div>
  );
};

export default Overview;